import { AsyncPipe, NgFor, NgIf } from '@angular/common';
import { Component, Input } from '@angular/core';
import { Observable } from 'rxjs';
import { filter, map, share, startWith, switchMap } from 'rxjs/operators';
import { PersonAttributes, TripAttributes } from '~app/models';
import { UserGroupService } from '~app/services/user-group.service';
import { DatetimeService } from '~app/services/datetime.service';

/** Distinguishes the kind of trip-log entry. */
export type TripLogLogType =
  | 'checkout'
  | 'checkin'
  | 'checkinPartial'
  | 'checkinQuickLink'
  | 'checkpointSubmitted'
  | 'lateTrip'
  | 'lateCheckpoint';

/** Internal Type used to consolidate different kinds of log data into one readable format */
export type TripLogProcessedData = {
  logType: TripLogLogType;
  /** Stable id of the source okay/checkpoint; keys per-row expand state. */
  id: string;
  datetime: Date;
  /** Short status label shown in the compact log (e.g. "Checked Out"). */
  status: string;
  description: string;
  /** Free-text note the user attached to the okay, shown in the expanded row. */
  note?: string;
  recordedByName: string;
  recordedByRole: string;
  recordedByDatetime: Date;
};

@Component({
  selector: 'ciao-trip-log-section-v3',
  imports: [NgIf, NgFor, AsyncPipe],
  templateUrl: './trip-log-section.component.html',
  styleUrl: './trip-log-section.component.less',
})
export class TripLogSectionComponent {
  constructor(
    private readonly userGroupService: UserGroupService,
    private readonly dateTimeService: DatetimeService
  ) {}

  /** "Sandi Ohleger" -> "S. Ohleger"; system/CIAO entries pass through. */
  abbreviateName(displayName?: string): string {
    if (!displayName) return '';
    const parts = displayName.trim().split(/\s+/);
    if (parts.length < 2) return parts[0] ?? '';
    return `${parts[0][0]}. ${parts[parts.length - 1]}`;
  }

  /**
   * Compact "7/27/26" in the browser's time zone. Uses browserDefaults, not
   * the saved preference, to match getTimePart/#1845 (all frontend datetimes
   * render in browser zone) and the "based on browser location" subtitle.
   */
  compactDate(date: Date): string {
    return new Date(date).toLocaleDateString('en-US', {
      timeZone: this.dateTimeService.browserDefaults.timeZone,
      year: '2-digit',
      month: 'numeric',
      day: 'numeric',
    });
  }

  /** Compact "03:00 PM" (always 12-hour w/ AM-PM), in the browser's time zone. */
  compactTime(date: Date): string {
    return new Date(date).toLocaleTimeString('en-US', {
      timeZone: this.dateTimeService.browserDefaults.timeZone,
      hour: '2-digit',
      minute: '2-digit',
      hour12: true,
    });
  }

  /**
   * Pill color class for a status, mirroring the trip-status badges:
   * Late* -> Late Trip, All Crew Checked In -> Closed Trip,
   * Checked Out and an on-time I'm Okay -> Open Trip (green).
   * The Late* check runs first, so "Late I'm OK Submitted" stays amber.
   * Other statuses stay plain text.
   */
  statusPillClass(status: string): string {
    if (status.startsWith('Late')) return 'status-late';
    if (status === 'All Crew Checked In' || status === 'Checked In') {
      return 'status-closed';
    }
    if (status === 'Checked Out') return 'status-open';
    return '';
  }

  /**
   * I'm Okay entries (submitted / late / missed check-ins) are shown with the
   * same status icon as the Scheduled I'm Okay Alerts table; trip-status entries
   * (check in / check out / late trip) keep the colored pill.
   */
  isImOkayEntry(item: TripLogProcessedData): boolean {
    return (
      item.logType === 'checkpointSubmitted' ||
      item.logType === 'lateCheckpoint'
    );
  }

  /** Status icon for an I'm Okay entry, matching the checkpoint table's set. */
  getLogStatusIcon(item: TripLogProcessedData): string {
    const base = 'assets/img/trip-2.0/checkpoint-status-icons/';
    if (item.logType === 'lateCheckpoint') return `${base}emergency_home.svg`;
    if (item.status === "Late I'm OK Submitted") return `${base}block.svg`;
    return `${base}task_alt.svg`;
  }

  @Input({ required: true }) trip$: Observable<TripAttributes>;
  logItems$: Observable<TripLogProcessedData[]>;

  /**
   * Which rows are expanded to show the full detail. Keyed by a stable
   * composite (not list index) so the expanded state survives the log being
   * recomputed when the trip re-emits.
   */
  private readonly expandedKeys = new Set<string>();

  private logKey(item: TripLogProcessedData): string {
    // Keyed on the source entity id (+ logType, since one okay yields several
    // entries) so every row is uniquely and stably identifiable — several
    // checkpoints satisfied by the same okay stay independently expandable.
    return `${item.logType}|${item.id}`;
  }

  isExpanded(item: TripLogProcessedData): boolean {
    return this.expandedKeys.has(this.logKey(item));
  }

  toggleExpanded(item: TripLogProcessedData): void {
    const key = this.logKey(item);
    if (this.expandedKeys.has(key)) {
      this.expandedKeys.delete(key);
    } else {
      this.expandedKeys.add(key);
    }
  }

  ngOnInit() {
    this.logItems$ = this.trip$.pipe(
      filter((trip) => !!trip),
      switchMap((trip) => {
        return this.userGroupService
          .getMembersByRole(trip.userGroupId, [
            'team_member',
            'supervisor',
            'dispatcher',
          ])
          .pipe(
            map((roles) => this.getPersonRoleList(roles, trip)),
            map((rolesByPersonId) => ({ trip, rolesByPersonId }))
          );
      }),
      map(({ trip, rolesByPersonId }) =>
        this.getLogItems(trip, rolesByPersonId)
      ),
      startWith([]),
      share()
    );
  }

  getPersonRoleList(
    teamRoles: {
      team_member: PersonAttributes[];
      supervisor: PersonAttributes[];
      dispatcher: PersonAttributes[];
    },
    trip: TripAttributes
  ) {
    let rolesByPersonId: { [personId: string]: string } = {};
    // lowest priority
    teamRoles.team_member.forEach(
      (team_member) => (rolesByPersonId[team_member.id] = 'Team Member')
    );
    teamRoles.dispatcher.forEach(
      (dispatcher) => (rolesByPersonId[dispatcher.id] = 'Dispatcher')
    );
    // priority over Team Member
    teamRoles.supervisor.forEach(
      (supervisor) => (rolesByPersonId[supervisor.id] = 'Supervisor')
    );
    // priority over Team Roles
    trip.crewMembers.forEach((meta) => {
      rolesByPersonId[meta.supervisorId] = 'Point of Contact';
      rolesByPersonId[meta.secondSupervisorId] = 'Point of Contact';
    });
    // priority over Point of Contact or Team Roles
    trip.crewMembers.forEach((meta) => {
      rolesByPersonId[meta.crewMemberId] = 'Crew Member';
    });

    return rolesByPersonId;
  }

  getLogItems(
    trip: TripAttributes,
    rolesByPersonId: { [personId: string]: string }
  ): TripLogProcessedData[] {
    let items: TripLogProcessedData[] = [];

    // Check Out log item(s)
    items.push(
      ...trip.okays
        .filter((okay) => okay.type === 'checkOut')
        .map((okay) => {
          let data: TripLogProcessedData = {
            logType: 'checkout',
            id: okay.id,
            datetime: new Date(okay.timeOkay),
            status: 'Checked Out',
            description: `Crew checked out.`,
            note: okay.notes || undefined,
            recordedByName: okay.recordedBy?.person?.displayName,
            recordedByRole:
              rolesByPersonId[okay.recordedBy?.personId] ?? 'Unknown Role',
            recordedByDatetime: new Date(okay.recordedAt),
          };

          return data;
        })
    );

    // Okays with no checkpoints
    items.push(
      ...trip.okays
        .filter((okay) => okay.type === 'okay' && okay.checkpoints.length === 0)
        .map((okay) => {
          let data: TripLogProcessedData = {
            logType: 'checkpointSubmitted',
            id: okay.id,
            datetime: new Date(okay.timeOkay),
            status: "I'm OK Submitted",
            description: `Unscheduled I\'m Okay submitted.`,
            note: okay.notes || undefined,
            recordedByName: okay.recordedBy?.person?.displayName,
            recordedByRole:
              rolesByPersonId[okay.recordedBy?.personId] ?? 'Unknown Role',
            recordedByDatetime: new Date(okay.recordedAt),
          };

          return data;
        })
    );

    // Check In log item(s)
    items.push(
      ...trip.okays
        .filter((okay) => okay.type === 'checkIn')
        .map((okay, index, allOkays) => {
          let data: TripLogProcessedData = {
            logType: 'checkin',
            id: okay.id,
            datetime: new Date(okay.timeOkay),
            status: 'Checked In',
            description: `(description under construction)`,
            recordedByName: okay.recordedBy?.person?.displayName,
            recordedByRole:
              rolesByPersonId[okay.recordedBy?.personId] ?? 'Unknown Role',
            recordedByDatetime: new Date(okay.recordedAt),
          };

          const lastCheckIn = allOkays.length === index + 1;
          const checkedInViaMagicLink =
            okay.notes === 'Checked In via Magic Link';
          const allCrewMembersSelected = trip.crewMembersProcessed.every(
            (person) =>
              okay.crewMembersSelected.find(
                (selected) => selected.id === person.id
              )
          );
          // `notes` doubles as the magic-link sentinel, so only treat it as a
          // real user note when it isn't that flag.
          data.note = checkedInViaMagicLink ? undefined : okay.notes || undefined;

          let names = okay.crewMembersSelected
            .map((person) => person.displayName)
            .join(', ');
          if (checkedInViaMagicLink) {
            data.description = 'Crew checked in via Quick Links.';
            data.logType = 'checkinQuickLink';
          } else if (allCrewMembersSelected && trip.tripStatus === 'Closed') {
            data.description = `All crew checked in.`;
            data.status = 'All Crew Checked In';
          } else if (lastCheckIn && trip.tripStatus === 'Closed') {
            data.description = `All remaining crew checked in.`;
            data.status = 'All Crew Checked In';
          } else {
            data.description = `${names} checked in via Individual Check In.`;
            data.logType = 'checkinPartial';
          }

          return data;
        })
    );

    const now = new Date();
    const endDate = new Date(trip.endDate);

    // Trip is / was Late for Check In log item.
    // The trip log is derived from the current trip state rather than a stored
    // event history, so this entry must not be tied solely to the live
    // `tripStatus`: once the crew checks in, the status flips to `Closed` and this
    // entry would otherwise disappear (the bug). Preserve it whenever the crew was
    // actually late — i.e. the check-in deadline (endDate) has passed AND either the
    // trip is still Late, or a check-in was recorded after that deadline.
    const checkedInAfterDeadline = trip.okays.some(
      (okay) => okay.type === 'checkIn' && new Date(okay.timeOkay) > endDate
    );
    if (endDate < now && (trip.tripStatus === 'Late' || checkedInAfterDeadline)) {
      items.push({
        logType: 'lateTrip',
        id: 'lateTrip',
        datetime: endDate,
        status: 'Late Check In',
        description: 'Crew is LATE for Check In.',
        recordedByName: 'CIAO',
        recordedByRole: '',
        recordedByDatetime: endDate,
      });
    }

    // Late and Missed Checkpoints should all give "Crew was Late to Checkpoint" log items
    items.push(
      ...trip.checkpoints
        .filter((checkpoint) => ['Late', 'Missed'].includes(checkpoint.status))
        .map((checkpoint) => {
          const cpEndDate = new Date(checkpoint.expectedTime);
          const item: TripLogProcessedData = {
            logType: 'lateCheckpoint',
            id: checkpoint.id,
            datetime: cpEndDate,
            status: "Late I'm OK",
            description: `Trip status changed to Late for missed "I\'m Okay" named '${checkpoint.description}'.`,
            recordedByName: 'CIAO',
            recordedByRole: '',
            recordedByDatetime: cpEndDate,
          };
          return item;
        })
    );

    // Submitted Checkpoints should give "Crew submitted a checkpoint" log items
    items.push(
      ...trip.checkpoints
        .filter((checkpoint) => ['Missed'].includes(checkpoint.status))
        .map((checkpoint) => {
          const okay = trip.okays.find((okay) => okay.id === checkpoint.okayId);
          if (!okay) {
            return null;
          }
          const okayDate = new Date(okay.timeOkay);
          const item: TripLogProcessedData = {
            logType: 'checkpointSubmitted',
            id: checkpoint.id,
            datetime: okayDate,
            status: "Late I'm OK Submitted",
            description: `Missed "I\'m Okay" submitted for '${checkpoint.description}'.`,
            note: okay.notes || checkpoint.notes || undefined,
            recordedByName: okay.recordedBy?.person?.displayName,
            recordedByRole:
              rolesByPersonId[okay.recordedBy?.personId] ?? 'Unknown Role',
            recordedByDatetime: new Date(okay.recordedAt),
          };
          return item;
        })
    );

    // Submitted Checkpoints should give "Crew submitted a checkpoint" log items
    items.push(
      ...trip.checkpoints
        .filter((checkpoint) => ['Submitted'].includes(checkpoint.status))
        .map((checkpoint) => {
          const okay = trip.okays.find((okay) => okay.id === checkpoint.okayId);
          if (!okay) {
            return null;
          }
          const okayDate = new Date(okay.timeOkay);
          const item: TripLogProcessedData = {
            logType: 'checkpointSubmitted',
            id: checkpoint.id,
            datetime: okayDate,
            status: "I'm OK Submitted",
            description: `Crew submitted scheduled "I\'m Okay" for '${checkpoint.description}'.`,
            // Notes are stored on the Okay when the user submits an I'm Okay.
            note: okay.notes || checkpoint.notes || undefined,
            recordedByName: okay.recordedBy?.person?.displayName,
            recordedByRole:
              rolesByPersonId[okay.recordedBy?.personId] ?? 'Unknown Role',
            recordedByDatetime: new Date(okay.recordedAt),
          };
          return item;
        })
    );

    // Sort by the date at the beginning of the log item display
    items.sort((a, b) => b.datetime.valueOf() - a.datetime.valueOf());

    return items;
  }
}
